import type { HrRangeQuery } from "../../ui/dateRange";
import type {
  HrAttendanceBadge,
  HrRequest,
  HrRequestStatus,
} from "../../types/hr.model";
import type { HrPeriodKey } from "../../types/hr.contract";

/**
 * Query keys for the Attendance screen and the request flows it hosts.
 * All under ["hr", "attendance", …] so `HR_KEYS.all` (["hr"]) refreshes them
 * after any cross-cutting write. Filter objects use `null` (never undefined)
 * for "no filter" so two spellings of the same filter hash identically.
 */

export interface HrRegisterFilters extends HrRangeQuery {
  badge: HrAttendanceBadge | null;
  q: string | null;
}

export interface HrSessionsFilters extends HrRangeQuery {
  q: string | null;
}

/**
 * The period of a request list. `from` / `to` only with CUSTOM (IST day keys);
 * null — never undefined — so the object is stable inside a query key.
 */
export interface HrRequestPeriod {
  period: HrPeriodKey;
  from: string | null;
  to: string | null;
}

export interface HrWfhFilters extends HrRequestPeriod {
  status: HrRequestStatus | null;
  q: string | null;
}

export interface HrLeaveFilters extends HrRequestPeriod {
  status: HrRequestStatus | null;
  q: string | null;
  leaveTypeId: number | null;
}

/** Attendance fixes (GET /attendance/corrections) — `status: null` = every status. */
export interface HrCorrectionsFilters {
  status: HrRequestStatus | null;
}

export const HR_ATTENDANCE_KEYS = {
  all: ["hr", "attendance"] as const,
  register: (f: HrRegisterFilters) => ["hr", "attendance", "register", f] as const,
  sessions: (f: HrSessionsFilters) => ["hr", "attendance", "sessions", f] as const,
  wfh: (f: HrWfhFilters) => ["hr", "attendance", "wfh", f] as const,
  /** Same endpoint, the SESSIONS list — its own cursor chain (tagged cursors). */
  wfhSessions: (f: HrWfhFilters) => ["hr", "attendance", "wfh-sessions", f] as const,
  leave: (f: HrLeaveFilters) => ["hr", "attendance", "leave", f] as const,
  corrections: (f: HrCorrectionsFilters) => ["hr", "attendance", "corrections", f] as const,
  filterOptions: ["hr", "attendance", "filter-options"] as const,
  punchState: ["hr", "attendance", "punch-state"] as const,
  balances: (employeeId: number | null) =>
    ["hr", "attendance", "leave-balances", employeeId ?? "self"] as const,
};

/**
 * Prefixes (for partial matching) of every list that holds HrRequest rows.
 * The Approvals list (["hr", "approvals"] — HR_KEYS.approvals) mixes all three
 * kinds, so every kind lists it.
 */
export const HR_REQUEST_LIST_PREFIXES: Record<
  HrRequest["kind"],
  readonly (readonly string[])[]
> = {
  LEAVE: [
    ["hr", "attendance", "leave"],
    ["hr", "dashboard", "leave-requests"],
    ["hr", "dashboard", "main"],
    ["hr", "approvals"],
  ],
  WFH: [
    ["hr", "attendance", "wfh"],
    ["hr", "approvals"],
  ],
  ATTENDANCE: [
    ["hr", "attendance", "corrections"],
    ["hr", "approvals"],
  ],
};
